import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { get, put } from 'aws-amplify/api';
import { Hub } from '@aws-amplify/core';

export interface Preferences {
  language: 'en' | 'fr' | 'es';
  dailyCalorieTarget: number;
}

interface PreferencesContextType {
  preferences: Preferences;
  updatePreferences: (newPreferences: Partial<Preferences>) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const defaultPreferences: Preferences = {
  language: 'en',
  dailyCalorieTarget: 2000
};

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preferences, setPreferences] = useState<Preferences>(defaultPreferences);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('Checking auth session...');
        await fetchAuthSession();
        console.log('Auth session found, setting authenticated');
        setIsAuthenticated(true);
      } catch (err) {
        console.log('Auth session check failed:', err);
        setIsAuthenticated(false);
      }
    };

    checkAuth();

    // Listen for auth events
    const listener = Hub.listen('auth', (data: { payload: { event: string } }) => {
      console.log('Auth event received:', data.payload.event);
      const { payload } = data;
      
      switch (payload.event) {
        case 'signedIn':
        case 'tokenRefresh':
          setIsAuthenticated(true);
          break;
        case 'signedOut':
        case 'tokenRefresh_failure':
          setIsAuthenticated(false);
          setPreferences(defaultPreferences);
          break;
      }
    });

    return () => listener();
  }, []);

  // Load preferences when authenticated
  useEffect(() => {
    console.log('Auth state changed, isAuthenticated:', isAuthenticated);
    if (isAuthenticated) {
      console.log('Loading preferences...');
      loadPreferences();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const loadPreferences = async () => {
    console.log('loadPreferences called, isAuthenticated:', isAuthenticated);
    if (!isAuthenticated) {
      console.log('Not authenticated, using default preferences');
      setPreferences(defaultPreferences);
      setLoading(false);
      return;
    }
    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();

      if (!authToken) {
        throw new Error('Not authenticated');
      }

      const restOperation = get({
        apiName: 'FoodTrackerAPI',
        path: '/preferences/',
        options: {
          headers: {
            Authorization: authToken,
            'Content-Type': 'application/json'
          }
        }
      });

      const { body } = await restOperation.response;
      const data = await body.json();
      console.log('Preferences loaded from API:', data);
      
      // Validate and type-check the response data
      if (data && typeof data === 'object' && 
          ('language' in data || 'dailyCalorieTarget' in data)) {
        console.log('Setting preferences with API data');
        setPreferences({
          ...defaultPreferences,
          ...data
        });
      } else {
        console.log('Invalid API response format, using defaults');
        setPreferences(defaultPreferences);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading preferences:', err);
      setError('Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const updatePreferences = async (newPreferences: Partial<Preferences>) => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();

      if (!authToken) {
        throw new Error('Not authenticated');
      }

      const updatedPreferences = { ...preferences, ...newPreferences };
      
      const restOperation = put({
        apiName: 'FoodTrackerAPI',
        path: '/preferences/',
        options: {
          headers: {
            Authorization: authToken,
            'Content-Type': 'application/json'
          },
          body: updatedPreferences
        }
      });

      await restOperation.response;
      setPreferences(updatedPreferences);
      setError(null);
    } catch (err) {
      console.error('Error updating preferences:', err);
      setError('Failed to update preferences');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <PreferencesContext.Provider value={{ preferences, updatePreferences, loading, error }}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};
